<template>
  <div v-if="isSignupStoppedNoticeShown" class="h-screen relative onboarding-font bg-white dark-mode:bg-dark-mode-90">
    <main class="h-full flex items-center justify-center px-6">
      <section class="max-w-2xl text-center">
        <a href="/" class="inline-flex mb-8 items-center justify-center">
          <app-logo class="dark-mode:hidden" />
          <dark-logo class="hidden dark-mode:block" />
        </a>
        <h1 class="font-bold text-32 md:text-56 leading-10 md:leading-70 mb-6 dark-mode:text-white">
          Sign ups are stopped
        </h1>
        <div class="mb-8 space-y-4 text-lg leading-7 text-gray-80 dark-mode:text-white">
          <p>
            Aerielab is no longer accepting new sign ups.
          </p>
        </div>
      </section>
    </main>
  </div>
  <div v-else class="h-screen relative onboarding-font">
    <div class="flex items-start justify-between h-full">
      <div :class="onboardingStep === 0 ? 'bg-white dark-mode:bg-dark-mode-90' : 'bg-blue-20 dark-mode:bg-dark-mode-80'" class="flex-1 h-full px-6 xl:px-0">
        <div class="h-full flex items-start md:items-center justify-center overflow-y-auto without-scrollbar py-6 md:py-0" v-if="onboardingStep === 0">
          <div class="text-center">
            <a href="/" class="flex md:hidden mb-6 items-center justify-center">
              <app-logo class="dark-mode:hidden" />
              <dark-logo class="hidden dark-mode:block" />
            </a>
            <h1 class="font-bold text-32 md:text-7xl leading-10 md:leading-91 mb-6 md:mb-8 dark-mode:text-white">Welcome to <span class="block text-56 leading-70 md:leading-91 md:text-7xl">Aerielab</span></h1>
            <p class="leading-6 mb-8 dark-mode:text-white" v-if="!isMobile">Aerielab makes it easy to plan, queue, and publish social posts.</p>
            <p class="leading-6 mb-8 dark-mode:text-white" v-else>Grow all your social media accounts from one dashboard.</p>
            <div class="flex md:hidden items-center justify-center w-3/4 mx-auto mt-8 mb-16">
            </div>
            <div v-if="isAccountBeingDeleted">
              <p class="leading-6 mb-8 dark-mode:text-white">
                Your account is currently being deleted.<br />
                If you want to signup for Aerielab again, please try again in a few minutes.
              </p>
            </div>
            <div v-else-if="isEmailLogin" id="firebaseui-auth-container" class="flex flex-col items-center space-y-4">
              <div class="flex flex-col w-full space-y-3">
                <input
                  type="text"
                  placeholder="Email"
                  v-model="email"
                />
                <input
                  type="password"
                  placeholder="Password"
                  v-model="password"
                />
              </div>
              <button
                class="button primary-button w-full md:w-1/2"
                @click="signInWithEmail"
                :disabled="isSigningIn"
              >
                {{ isSigningIn ? 'Signing in...' : 'Sign in' }}
              </button>
              <p class="text-center text-base text-gray-80 dark-mode:text-white mt-6">
                By signing in, I agree to the
                <a href="https://aerielab.co/terms-of-service/" class="text-main-color-100" target="_blank">TOS</a>
                and
                <a href="https://aerielab.co/privacy-policy/" class="text-main-color-100" target="_blank">Aerielab Privacy Policy</a>.
              </p>
            </div>
            <div v-else>
              <button
                class="button primary-button w-full md:w-auto"
                @click.prevent="signInWithTwitter()"
                :disabled="isSigningIn"
              >
                <twitter class="mr-4" />
                {{ isSigningIn ? 'Signing in...' : 'Sign in with Twitter' }}
                <div id="firebaseui-auth-container" class="hidden" v-if="loginMode == 'twitter' && !isAccountBeingDeleted"></div>
              </button>
              <p class="text-center text-base text-gray-80 dark-mode:text-white mt-6">
                By signing in, I agree to the
                <a href="https://aerielab.co/terms-of-service/" class="text-main-color-100" target="_blank">TOS</a>
                and
                <a href="https://aerielab.co/privacy-policy/" class="text-main-color-100" target="_blank">Aerielab Privacy Policy</a>.
              </p>
            </div>
          </div>
        </div>
      </div>
      <div :class="onboardingStep === 0 ? 'bg-blue-20 dark-mode:bg-dark-mode-80' : 'bg-white dark-mode:bg-dark-mode-90'" class="flex-1 h-full relative px-6 xl:px-0 hidden md:block">
        <div class="flex flex-col items-center justify-center h-full" v-if="onboardingStep === 0">
          <img
            class=" h-480 w-4/5 object-contain rounded-lg lg:object-none 2xl:w-1/2 md:h-1/2"
            src="/images/aerielab-schedule-login.png"
          >
          <p class="dark-mode:text-white text-center my-2 w-3/5 2xl:w-1/2 lg:text-2xl md:text-lg">
            {{ loginImageDescription }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
  /* global ga */
  import { checkTheme } from '@/util/checkTheme';
  import firebase from 'firebase/compat/app';
  import 'firebase/compat/firestore';
  import 'firebase/compat/auth';
  import * as firebaseui from 'firebaseui';
  import MobileMixin from '@/views/Mixins/MobileMixin.vue';
  import controller from '@/controller';
  const fb = require('../../firebase');
  import twitter from '../../svgs/twitter';
  import AppLogo from '../../svgs/logo';
  import DarkLogo from '../../svgs/darkLogo';

  export default {
    data() {
      return {
        loginMode: 'twitter',
        isLoginWithTwitterExplanationShown: false,
        isPostTwitterAuth: false,
        isAccountBeingDeleted: false,
        onboardingStep: 0,
        isAutoTweet: true,
        tenTweetsKeeped: false,
        isSigningIn: false,
        isEmailLogin: false,
        isSignupStoppedNoticeShown: false,
        email: null,
        password: null,
      };
    },
    components: {
      twitter,
      AppLogo,
      DarkLogo
    },
    mounted: function () {
      this.isEmailLogin = this.$route.query.type === 'email';
      const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      checkTheme(isDark);
      const authUI = new firebaseui.auth.AuthUI(firebase.auth());
      if (authUI.isPendingRedirect()) {
        this.isPostTwitterAuth = true;
      }
      const that = this;
      authUI.start('#firebaseui-auth-container', {
        signInOptions: [firebase.auth.TwitterAuthProvider.PROVIDER_ID],
        signInFlow: 'popup',
        callbacks: {
          uiShown: function () {
          },
          signInSuccessWithAuthResult: function (authResult) {
            const ref = fb.usersCollection.doc(authResult.user.uid);

            ref.get().then((doc) => {
              const userProfile = doc.data();

              if (userProfile && userProfile.isBeingDeleted === true) {
                firebase
                  .auth()
                  .signOut()
                  .then(() => {
                    that.isAccountBeingDeleted = true;
                    localStorage.clear();
                  });
              } else if (!userProfile) {
                that.createUserOrShowSignupStoppedNotice(authResult);
              } else {
                that.updateUserAccessTokens(authResult, doc).then(() => {
                  const nextURL = localStorage.getItem('nextURL') || '/';
                  localStorage.removeItem('nextURL');
                  that.$router.push(nextURL);
                });
              }
            });
          },
          signInFailure: function (error) {
            console.error('An error has occurred while signing in with Twitter.', error);
            that.isSigningIn = false;
          },
        },
      });
    },
    computed: {
      loginStatusText() {
        return this.$route.path === '/free'
          ? 'Use Aerielab for free now'
          : 'Start your free trial now';
      },
      loginImageDescription() {
        return 'Plan, queue, and publish on your schedule.';
      },
    },
    methods: {
      showLoginWithTwitterExplanation() {
        this.isLoginWithTwitterExplanationShown = true;
        try {
          ga('send', 'pageview', { page: '/why-login', title: 'Twitter login explanation' });
        } catch (error) {
          console.log('An error has occurred while sending "pageview" to Google Analytics.', error);
        }
      },
      updateUserAccessTokens: (authResult, doc) => {
        const update = {
          twitterAccessTokenKey: authResult.credential.accessToken,
          twitterAccessTokenSecret: authResult.credential.secret,
          // Clear even when X reissues the same tokens; fence older in-flight probes.
          xApiAuth: null,
          twitterAccessTokenReconnectedAt: new Date(),
          twitterAccessTokenValidity: {
            areTokensValid: true,
            lastUpdatedAt: new Date(),
          },
        };
        return doc.ref.update(update);
      },
      isAccountCreationAllowed() {
        return (
          Object.prototype.hasOwnProperty.call(this.$route.query, 'allowAccountCreation') ||
          localStorage.getItem('allowAccountCreation') === 'true'
        );
      },
      createUserOrShowSignupStoppedNotice(authResult) {
        if (!this.isAccountCreationAllowed()) {
          this.showSignupStoppedNotice();
          return;
        }

        return controller.users.create(authResult.user, this.getUserDataFromAuthResult(authResult))
          .then(() => {
            localStorage.removeItem('allowAccountCreation');
            const nextURL = localStorage.getItem('nextURL') || '/setup';
            localStorage.removeItem('nextURL');
            this.$router.push(nextURL);
          })
          .catch((error) => {
            console.error('An error has occurred while creating a user account.', error);
            this.showSignupStoppedNotice();
          });
      },
      getUserDataFromAuthResult(authResult) {
        const profile = authResult.additionalUserInfo.profile;

        return {
          email: profile.email || '',
          followersCount: profile.followers_count || 0,
          followingCount: profile.friends_count || 0,
          hasSignedUpFromMobile: this.isMobile,
          name: profile.name,
          photoURL: profile.profile_image_url_https,
          source: this.$route.query.source || null,
          twitterAccessTokenKey: authResult.credential.accessToken,
          twitterAccessTokenSecret: authResult.credential.secret,
          twitterId: profile.id_str,
          uid: authResult.user.uid,
          username: profile.screen_name,
        };
      },
      showSignupStoppedNotice() {
        this.isSignupStoppedNoticeShown = true;
        this.isSigningIn = false;
        firebase.auth().signOut().catch((error) => {
          console.error('An error has occurred while signing out after a stopped signup attempt.', error);
        });
      },
      signInWithTwitter() {
        this.isSigningIn = true;

        const firebaseUIButton = document.querySelector('#firebaseui-auth-container button');
        if (!firebaseUIButton) {
          console.error('Unable to start Twitter sign-in because the FirebaseUI button is not available.');
          this.isSigningIn = false;
          return;
        }

        firebaseUIButton.click();
      },
      signInWithEmail() {
        this.isSigningIn = true;
        return firebase.auth().signInWithEmailAndPassword(this.email, this.password)
          .then(() => {
            setTimeout(() => {
              const nextURL = localStorage.getItem('nextURL') || '/';
              localStorage.removeItem('nextURL');
              this.$router.push(nextURL);
            }, 3000);
          })
          .catch((error) => {
            console.log(error);
            this.isSigningIn = false;
          });
      },
    },
    mixins: [MobileMixin],
  };
</script>
